import { useCallback, useMemo, useState } from 'react';
import { StepData } from '../types';
import { CreateStepArgs } from './types';

export const useCreateStepper = <const T>(initialSteps: ReadonlyArray<StepData<T>>, initialId?: T) => {
  if (!initialSteps.length) {
    throw new Error('initialSteps must contain at least one step');
  }

  const [steps, setSteps] = useState<CreateStepArgs<T>[]>(() =>
    initialSteps.map((step) => ({
      ...step,
      isCompleted: false,
    }))
  );

  const [currentStepId, setCurrentStepId] = useState(() => initialId ?? steps[0].id);

  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex((step) => step.id === currentStepId);

    if (index === -1) {
      throw new Error(`Step with id ${currentStepId} not found`);
    }

    return index;
  }, [steps, currentStepId]);

  const isLastStep = useMemo(() => currentStepIndex === steps.length - 1, [currentStepIndex, steps.length]);

  const isAllStepsCompleted = useMemo(() => steps.every((step) => step.isCompleted), [steps]);

  const goToStep = useCallback(
    (id: T) => {
      setCurrentStepId(id);
    },
    [setCurrentStepId]
  );

  const goToNextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepId(steps[currentStepIndex + 1].id);
    }
  }, [setCurrentStepId, steps.length, currentStepIndex]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepId(steps[currentStepIndex - 1].id);
    }
  }, [setCurrentStepId, steps.length, currentStepIndex]);

  const completeStep = useCallback(
    (id: T) => {
      setSteps((prev) => prev.map((step) => (step.id === id ? { ...step, isCompleted: true } : { ...step })));
    },
    [setSteps]
  );

  const completeCurrentStep = useCallback(() => {
    completeStep(currentStepId);
  }, [completeStep, currentStepId]);

  /** Back to the first step with every completed flag cleared — for restarting a flow from scratch. */
  const reset = useCallback(() => {
    setSteps(initialSteps.map((step) => ({ ...step, isCompleted: false })));
    setCurrentStepId(initialId ?? initialSteps[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    steps,
    currentStepId,
    currentStepIndex,
    isLastStep,
    isAllStepsCompleted,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    completeStep,
    completeCurrentStep,
    reset,
  };
};
